import type { WakeupCall, WakeupDisplayStatus } from '../types/wakeup'
import { getWakeupInstantIso } from './formatting'

export type WakeupHistorySortField =
  | 'room_number'
  | 'room_name'
  | 'attempt_count'
  | 'wuc_timestamp'
  | 'activation_timestamp'
  | 'display_status'
  | 'origination'

export type WakeupHistorySortDirection = 'asc' | 'desc'

export interface WakeupHistorySort {
  field: WakeupHistorySortField
  direction: WakeupHistorySortDirection
}

export const DEFAULT_WAKEUP_HISTORY_SORT: WakeupHistorySort = {
  field: 'wuc_timestamp',
  direction: 'desc',
}

const WAKEUP_HISTORY_SORT_FIELDS = new Set<WakeupHistorySortField>([
  'room_number',
  'room_name',
  'attempt_count',
  'wuc_timestamp',
  'activation_timestamp',
  'display_status',
  'origination',
])

const DISPLAY_STATUS_ORDER: Record<WakeupDisplayStatus, number> = {
  scheduled: 0,
  tried: 1,
  cleared: 2,
  canceled: 3,
}

const compareNumbers = (a: number, b: number): number => a - b

const getEpoch = (value: string | null | undefined): number => {
  if (!value) return 0
  const epoch = Date.parse(value)
  return Number.isNaN(epoch) ? 0 : epoch
}

const getActivationInstantIso = (wakeupCall: WakeupCall): string | null => {
  const activation = wakeupCall.activation_timestamp
  if (activation?.iso_8601_utc) return activation.iso_8601_utc
  if (activation?.timestamp) return activation.timestamp
  return null
}

const compareByField = (
  left: WakeupCall,
  right: WakeupCall,
  field: WakeupHistorySortField,
  collator: Intl.Collator,
): number => {
  switch (field) {
    case 'room_number':
      return collator.compare(left.room_number ?? '', right.room_number ?? '')
    case 'room_name':
      return collator.compare(left.room_name ?? '', right.room_name ?? '')
    case 'attempt_count':
      return compareNumbers(left.attempt_count, right.attempt_count)
    case 'wuc_timestamp':
      return compareNumbers(
        getEpoch(getWakeupInstantIso(left)),
        getEpoch(getWakeupInstantIso(right)),
      )
    case 'activation_timestamp':
      return compareNumbers(
        getEpoch(getActivationInstantIso(left)),
        getEpoch(getActivationInstantIso(right)),
      )
    case 'display_status':
      return compareNumbers(
        DISPLAY_STATUS_ORDER[left.display_status ?? 'scheduled'] ??
          Number.MAX_SAFE_INTEGER,
        DISPLAY_STATUS_ORDER[right.display_status ?? 'scheduled'] ??
          Number.MAX_SAFE_INTEGER,
      )
    case 'origination':
      return compareNumbers(left.origination, right.origination)
  }
}

export const parseWakeupHistorySort = (
  value: string | null | undefined,
): WakeupHistorySort => {
  if (!value) return DEFAULT_WAKEUP_HISTORY_SORT

  const direction: WakeupHistorySortDirection = value.startsWith('-')
    ? 'desc'
    : 'asc'
  const field = value.replace(/^-/, '') as WakeupHistorySortField

  if (!WAKEUP_HISTORY_SORT_FIELDS.has(field)) {
    return DEFAULT_WAKEUP_HISTORY_SORT
  }

  return { field, direction }
}

export const toWakeupHistorySortParam = (sort: WakeupHistorySort): string => {
  return sort.direction === 'desc' ? `-${sort.field}` : sort.field
}

export const sortWakeupHistoryCalls = (
  wakeupCalls: WakeupCall[],
  sort: WakeupHistorySort,
  locale = 'en-US',
): WakeupCall[] => {
  const collator = new Intl.Collator(locale, {
    numeric: true,
    sensitivity: 'base',
  })

  return [...wakeupCalls].sort((left, right) => {
    const comparison = compareByField(left, right, sort.field, collator)
    return sort.direction === 'asc' ? comparison : -comparison
  })
}
